/**
 * 📝 SUBMIT TESTIMONIAL MODAL — COACHDIA LIGHT PREMIUM (Iter298)
 * ==============================================================
 * Auth required. Status='pending' à la soumission, modération obligatoire avant publication.
 */

import { getBackendUrl } from '../config/environment';
import React, { useState } from 'react';
import { safeFetchJSON } from '../utils/apiHelpers';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SubmitTestimonialModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    rating: 0,
    message: '',
    role: '',
    company: '',
    company_size: '50-250',
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [consent, setConsent] = useState(false);

  const resetAndClose = () => {
    onClose();
    setSuccess(false);
    setFormData({ rating: 0, message: '', role: '', company: '', company_size: '50-250' });
    setConsent(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.rating === 0) return setError('Veuillez sélectionner une note.');
    if (formData.message.length < 30) return setError('Le témoignage doit contenir au moins 30 caractères.');
    if (formData.message.length > 500) return setError('Le témoignage ne doit pas dépasser 500 caractères.');
    if (formData.role.length < 2) return setError('Veuillez indiquer votre poste.');
    if (!consent) return setError('Veuillez accepter la publication après modération.');

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await safeFetchJSON(`${getBackendUrl()}/api/testimonials/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      setSuccess(true);
      setTimeout(resetAndClose, 3000);
    } catch (err) {
      setError(err.message || 'Erreur de connexion au serveur. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ background: 'rgba(28,17,71,0.45)', backdropFilter: 'blur(6px)' }}
        data-testid="submit-testimonial-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.2 }}
          className="bg-[#FAF7EF] rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#EDE7D7]"
          style={{ boxShadow: '0 24px 60px rgba(28,17,71,0.20)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#EDE7D7]">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold mb-1">
                Témoignage
              </p>
              <h2 className="text-2xl font-serif text-[#1C1147]">Partagez votre expérience</h2>
            </div>
            <button
              type="button"
              onClick={resetAndClose}
              className="text-[#5A5266] hover:text-[#1C1147] transition-colors"
              aria-label="Fermer"
              data-testid="modal-close-btn"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {success ? (
            <div className="p-10 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
              >
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-[#B7901F]" />
              </motion.div>
              <h3 className="text-xl font-serif text-[#1C1147] mb-2">
                Merci pour votre témoignage !
              </h3>
              <p className="text-[#5A5266]">Votre témoignage sera publié après validation.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="testimonial-form">
              {/* Rating */}
              <div>
                <label className="block text-sm font-semibold text-[#1C1147] mb-3">
                  Votre note
                </label>
                <div className="flex space-x-2" role="radiogroup" aria-label="Note de 1 à 5 étoiles">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="transition-transform hover:scale-110"
                      aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
                      data-testid={`rating-star-${star}`}
                    >
                      <Star
                        className="w-9 h-9"
                        style={{
                          fill: star <= (hoverRating || formData.rating) ? '#D4AF37' : 'transparent',
                          color: star <= (hoverRating || formData.rating) ? '#D4AF37' : '#A89C7B',
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold text-[#1C1147] mb-2">
                  Votre témoignage <span className="text-[#B7901F]">*</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Partagez votre expérience avec CoachDIA… (30-500 caractères)"
                  rows={5}
                  data-testid="testimonial-message-input"
                  className="w-full px-4 py-3 bg-white border border-[#EDE7D7] rounded-xl text-[#1C1147] placeholder-[#A89C7B] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent resize-none"
                />
                <p className="text-xs text-[#7A6F58] mt-1">{formData.message.length}/500 caractères</p>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-semibold text-[#1C1147] mb-2">
                  Votre poste <span className="text-[#B7901F]">*</span>
                </label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="Ex : Directrice Produit"
                  data-testid="testimonial-role-input"
                  className="w-full px-4 py-3 bg-white border border-[#EDE7D7] rounded-xl text-[#1C1147] placeholder-[#A89C7B] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                />
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-semibold text-[#1C1147] mb-2">
                  Entreprise / Secteur <span className="text-[#7A6F58] font-normal">(optionnel)</span>
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Ex : TechFlow"
                  data-testid="testimonial-company-input"
                  className="w-full px-4 py-3 bg-white border border-[#EDE7D7] rounded-xl text-[#1C1147] placeholder-[#A89C7B] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                />
              </div>

              {/* Company Size */}
              <div>
                <label className="block text-sm font-semibold text-[#1C1147] mb-2">
                  Taille de l'entreprise <span className="text-[#B7901F]">*</span>
                </label>
                <select
                  value={formData.company_size}
                  onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
                  data-testid="testimonial-company-size-select"
                  className="w-full px-4 py-3 bg-white border border-[#EDE7D7] rounded-xl text-[#1C1147] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                >
                  <option value="<50">Moins de 50 employés</option>
                  <option value="50-250">50 à 250 employés</option>
                  <option value=">250">Plus de 250 employés</option>
                </select>
              </div>

              {/* Consent */}
              <label className="flex items-start gap-3 text-sm text-[#3A3252] cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="testimonial-consent-checkbox"
                  className="mt-0.5 w-4 h-4 rounded border-[#A89C7B] text-[#D4AF37] focus:ring-[#D4AF37]"
                />
                <span>
                  J'accepte que mon témoignage soit publié sur le site après{' '}
                  <strong className="text-[#1C1147]">modération par l'équipe CoachDIA</strong>.
                </span>
              </label>

              {/* Error */}
              {error ? (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center space-x-2 p-4 bg-[#FFF1ED] border border-[#F4A98C] rounded-xl"
                  data-testid="testimonial-error"
                >
                  <AlertCircle className="w-5 h-5 text-[#B85432] flex-shrink-0" />
                  <p className="text-sm text-[#B85432]">{error}</p>
                </motion.div>
              ) : null}

              {/* Buttons */}
              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={resetAndClose}
                  className="flex-1 px-6 py-3 bg-white text-[#1C1147] rounded-full transition-all border border-[#EDE7D7] hover:bg-[#FFF8E0] font-semibold"
                  data-testid="modal-cancel-btn"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 rounded-full font-bold text-[#1C1147] transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #D4AF37, #F3D060)',
                    boxShadow: '0 6px 20px rgba(212,175,55,0.30)',
                  }}
                  data-testid="modal-submit-btn"
                >
                  {loading ? 'Envoi en cours…' : 'Soumettre mon témoignage'}
                </button>
              </div>

              {!user ? (
                <p className="text-xs text-center text-[#7A6F58]">
                  Vous devez être connecté pour soumettre un témoignage.
                </p>
              ) : null}
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SubmitTestimonialModal;
